import { isAdminEmail } from '../../config/admin-emails';
import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { Subject, takeUntil, filter } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { DataService } from '../../services/data.service';
import { UserDocument } from '../../interfaces/user-document.interface';

interface NavItem {
  label: string;
  icon: string;
  path: string;
  roles?: string[];  // required roles (any match)
  adminOnly?: boolean;
  always?: boolean;
  memberOnly?: boolean;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

@Component({
  selector: 'dc-shell',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './dc-shell.component.html',
  styleUrls: ['./dc-shell.component.css'],
})
export class DcShellComponent implements OnInit, OnDestroy {
  userData: UserDocument | null = null;
  sidebarOpen = false;
  showUserMenu = false;
  currentPageTitle = '';

  private destroy$ = new Subject<void>();

  readonly navGroups: NavGroup[] = [
    {
      label: 'Tổng quan',
      items: [
        { label: 'Trang chủ', icon: '🏠', path: '/', always: true },
      ],
    },
    {
      label: 'Quản lý',
      items: [
        { label: 'Quản lý hồ sơ', icon: '📋', path: '/quanLyHoSo', memberOnly: true },
        { label: 'Quản lý thành viên', icon: '👥', path: '/quanLyThanhVien', memberOnly: true },
        { label: 'Báo cáo sự kiện', icon: '📊', path: '/interaction-report', memberOnly: true },
      ],
    },
    {
      label: 'Chuyên biệt',
      items: [
        { label: 'Nhà thuốc', icon: '💊', path: '/nhaThuocTong', adminOnly: true },
        { label: 'Phòng xét nghiệm', icon: '🧪', path: '/labhouse', roles: ['labhouse'] },
      ],
    },
  ];

  constructor(
    private dataService: DataService,
    public authService: AuthService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.dataService.userData$.pipe(takeUntil(this.destroy$)).subscribe(d => {
      this.userData = d;
    });

    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      takeUntil(this.destroy$),
    ).subscribe(() => {
      this.sidebarOpen = false;
      this.showUserMenu = false;
    });
  }

  get member(): boolean {
    return (this.userData?.diamonds ?? 0) >= 5000;
  }

  get isAdmin(): boolean {
    return isAdminEmail(this.userData?.email);
  }

  get avatarInitial(): string {
    return this.userData?.email?.[0]?.toUpperCase() ?? 'U';
  }

  showItem(item: NavItem): boolean {
    if (item.always) return true;
    if (item.adminOnly) return this.isAdmin;
    if (item.roles?.length) return item.roles.some(r => this.userData?.role?.includes(r));
    if (item.memberOnly) return this.member;
    return true;
  }

  getVisibleItems(group: NavGroup): NavItem[] {
    return group.items.filter(item => this.showItem(item));
  }

  isActive(path: string): boolean {
    if (path === '/') return this.router.url === '/';
    return this.router.url.startsWith(path);
  }

  navigate(path: string): void {
    this.router.navigateByUrl(path);
  }

  signOut(): void {
    this.authService.signOut();
  }

  toggleSidebar(): void {
    this.sidebarOpen = !this.sidebarOpen;
  }

  @HostListener('document:click', ['$event'])
  onDocClick(e: Event): void {
    const target = e.target as HTMLElement;
    if (!target.closest('.dc-shell__user-btn') && !target.closest('.dc-shell__user-menu')) {
      this.showUserMenu = false;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
